import { styled } from '@mui/material/styles';
import { Box, Typography } from '@mui/material';

const dashedBox = theme => ({
  background: '#f2f2f2',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  textAlign: 'center',
});

/* ——— outer layout ——— */
export const DashboardContainer = styled(Box)(({ theme }) => ({
  // display: 'grid',
  // gridTemplateRows: 'auto auto',
  gap: theme.spacing(2),
  padding: theme.spacing(2),
  width:'100%',
  margin:'auto',
  [theme.breakpoints.down('sm')]: {
    width:'95%',
    padding: theme.spacing(1),
    marginLeft:'8%'
  },
  [theme.breakpoints.between('sm', 'md')]: {
    flexDirection: 'row',
    width: '100%',
    marginLeft:'2%',
    padding: theme.spacing(2),
  },
  [theme.breakpoints.between('md', 'lg')]: {
    flexDirection: 'row',
    width: '95%',
    padding: theme.spacing(2),
  },
  // ✅ phone landscape override
  '@media (orientation: landscape) and (max-height: 450px)': {
    marginLeft:'2%'
  },
}));

export const DividerLine = styled(Box)(({ theme }) => ({
  borderTop: '1px solid #e5e7eb', // gray-200
  marginTop: theme.spacing(1),
}));

/* ——— row 1 ——— */
// export const FirstRow = styled(Box)(({ theme }) => ({
//   display: 'grid',
//   gridTemplateColumns: '40% 46% 10%',
//   justifyContent:'space-between',
//   marginBottom:'24px',
// }));

export const FirstRow = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: '40% 46% 10%',
  justifyContent: 'space-between',
  marginBottom: '24px',
  gap: theme.spacing(2),

   // ✅ TABLET (iPad): stack sections into rows
  [theme.breakpoints.between('sm', 'lg')]: {
    gridTemplateColumns: '1fr',
    gridTemplateAreas: `
      "stats"
      "tiles"
      "stock"
    `,
  },

  // ✅ MOBILE: stack
  [theme.breakpoints.down('sm')]: {
    gridTemplateColumns: '1fr',
    gridTemplateAreas: `
      "stats"
      "tiles"
      "stock"
    `,
  },

  // ✅ phone landscape override (small height)
  '@media (orientation: landscape) and (max-height: 450px)': {
    gridTemplateColumns: '1fr',
    gridTemplateAreas: `
      "stats"
      "tiles"
      "stock"
    `,
  },
}));


export const TwentyWidget = styled(Box)(({ theme }) => dashedBox(theme));

export const GridOfSix = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(2, 1fr)',
  gridTemplateRows: 'repeat(1, auto)',
  gap: theme.spacing(2),
  alignContent: 'space-between'
}));

export const StockColumn = styled(Box)(({ theme }) => ({
  display: 'grid',
  gap: theme.spacing(2),
  background: 'transparent',

  // ✅ Desktop: sidebar style (one per row)
  gridTemplateColumns: '1fr',

  // ✅ Tablet (iPad): 2 per row → flows into next row
  [theme.breakpoints.between('sm', 'lg')]: {
    gridTemplateColumns: 'repeat(3, 1fr)',
  },

  // ✅ Mobile: stack
  [theme.breakpoints.down('sm')]: {
    gridTemplateColumns: '1fr',
  },
}));


export const SmallWidget = styled(Box)(({ theme }) => dashedBox(theme));

/* ——— row 2 ——— */
export const SecondRow = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: '63% 34%',
  justifyContent:'space-between',

   '& > *': { minWidth: 0 },

  // ✅ tablets + phones: stack
  [theme.breakpoints.down('md')]: {
    gridTemplateColumns: '1fr',
    justifyContent: 'stretch',
    gap: theme.spacing(4),
  },

  // ✅ phone landscape override
  '@media (orientation: landscape) and (max-height: 450px)': {
    gridTemplateColumns: '1fr',
    justifyContent: 'stretch',
    gap:'16px'
  },
  
}));

export const MainSquare = styled(Box)(({ theme }) => ({
  ...dashedBox(theme),
  aspectRatio: '1 / 1',

  [theme.breakpoints.down('md')]: {
    aspectRatio: 'auto',
    height: 'auto',
    minHeight: 420,   // pick a nice minimum so it looks solid
  },

  [theme.breakpoints.down('sm')]: {
    minHeight: 360,
    marginBottom: '16px',
  },
}));

export const SideGrid = styled(Box)(({ theme }) => ({
  display: 'grid',
  // gridTemplateRows: '1fr 1fr',
  gap: theme.spacing(2),
  height:'auto !important'
}));

export const SideWidget = styled(Box)(({ theme }) => ({
  height:'auto !important'
}));

const dashed = {
  border: '1px solid transparent',
};

export const CardWrap = styled(Box)(({ theme }) => ({
  background: '#fff',
  borderRadius: 12,
  boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  width:'100%'
}));

/* ─────────── Tabs ─────────── */

export const TabsBar = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(2, 3, 0, 3),
  gap: theme.spacing(3),
  borderBottom: `1px solid ${theme.palette.grey[200]}`,
}));

export const TabItem = styled(Box)(({ theme, $active }) => ({
  ...dashed,
  cursor: 'pointer',
  fontWeight: 600,
  color: $active ? theme.palette.primary.main : theme.palette.text.secondary,
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  position: 'relative',
  paddingBottom: theme.spacing(1),
  borderBottom: $active ? `2px solid ${theme.palette.primary.main}` : '2px solid transparent',
}));

export const BadgeDot = styled('span')(({ theme }) => ({
  fontSize: 12,
  fontWeight: 600,
  color: theme.palette.common.white,
  background: '#fca5a5', // light red
  borderRadius: 12,
  padding: '0 6px',
}));

/* ─────────── Panel ─────────── */

export const Panel = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
  overflowY: 'auto',
}));

export const SectionTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 700,
  fontSize: '1.25rem',
  marginBottom: theme.spacing(3),
}));

/* ─────────── Table ─────────── */

export const TableHeadRow = styled('tr')(({ theme }) => ({
  borderBottom: `1px solid ${theme.palette.grey[300]}`,
}));

export const TableRowStyled = styled('tr')(({ theme }) => ({
  borderBottom: `1px solid ${theme.palette.grey[300]}`,
}));

export const Cell = styled('td')(({ theme }) => ({
  padding: theme.spacing(2, 0),
  verticalAlign: 'middle',
  '&:first-of-type': { display: 'flex', gap: theme.spacing(2), alignItems: 'center' },
  '&:nth-of-type(1)': { width: '30%' },
}));

/* ─────────── Helpers ─────────── */

export const Pill = styled('span')(({ theme }) => ({
  background: '#fee2e2',
  color: theme.palette.error.main,
  padding: '2px 10px',
  borderRadius: 20,
  fontSize: 12,
  fontWeight: 600,
}));

export const IconSquare = styled(Box)(({ theme }) => ({
  height: 32,
  width: 32,
  borderRadius: 8,
  background: '#e0e7ff', // placeholder blue
  flexShrink: 0,
}));